import { useState } from 'react'
import { Dialog } from 'radix-ui'
import { Combobox } from '@/components/common/Combobox'
import { useDeleteBoardColumn } from '../hooks'
import type { Board, BoardColumnDto } from '../types'

interface DeleteColumnDialogProps {
  board: Board
  column: BoardColumnDto
  onClose: () => void
}

/**
 * Pyta o potwierdzenie usunięcia kolumny. Gdy kolumna ma karty, wymaga wskazania
 * docelowej kolumny - inaczej backend zwróci 409 (ColumnHasCardsException).
 */
export function DeleteColumnDialog({ board, column, onClose }: DeleteColumnDialogProps) {
  const remove = useDeleteBoardColumn(board.id)
  const hasCards = column.cards.length > 0
  const otherColumns = board.columns.filter((c) => c.id !== column.id)
  const [moveTo, setMoveTo] = useState<number | null>(
    hasCards ? otherColumns[0]?.id ?? null : null,
  )

  const submit = () => {
    remove.mutate(
      {
        columnId: column.id,
        moveCardsToColumnId: hasCards ? moveTo ?? undefined : undefined,
      },
      { onSuccess: onClose },
    )
  }

  const canSubmit = !hasCards || moveTo != null

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose() }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/40" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg p-5 w-[420px] shadow-xl">
          <Dialog.Title className="text-base font-semibold">Usuń kolumnę "{column.name}"</Dialog.Title>
          <div className="mt-4 flex flex-col gap-3 text-sm">
            {hasCards ? (
              <>
                <p className="text-slate-700">
                  Kolumna ma <strong>{column.cards.length}</strong> {column.cards.length === 1 ? 'kartę' : 'kart'}.
                  Przenieś je do innej kolumny:
                </p>
                {otherColumns.length === 0 ? (
                  <p className="text-red-600">
                    Nie ma innej kolumny w tym boardzie do której można przenieść karty.
                    Najpierw dodaj kolumnę.
                  </p>
                ) : (
                  <Combobox<number>
                    value={moveTo}
                    onChange={(v) => setMoveTo(v)}
                    options={otherColumns.map((c) => ({ value: c.id, label: c.name }))}
                    placeholder="Wybierz kolumnę docelową..."
                  />
                )}
              </>
            ) : (
              <p className="text-slate-700">Kolumna jest pusta. Czy na pewno chcesz ją usunąć?</p>
            )}
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={onClose} className="px-3 py-1 text-sm border rounded">Anuluj</button>
              <button
                onClick={submit}
                disabled={!canSubmit || remove.isPending}
                className="px-3 py-1 text-sm bg-red-600 text-white rounded disabled:opacity-50"
              >
                Usuń
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
